import { AuthButtonState, ContentOutletComponent, HasSubscriptionsToClose, PopoverMenuItem } from '@agingplan';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BreakpointWatcherService } from 'src/app/services/breakpoint-watcher.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../../../../services/authentication.service';

/**
 * PublicTopNavigationComponent handles the top navigation bar for public pages.
 * It manages navigation items, authentication state, and responsive behavior.
 * Extends HasSubscriptionsToClose to ensure proper cleanup of subscriptions.
 *
 * @implements {AfterViewInit}
 */
@Component({
  selector: 'public-top-navigation',
  templateUrl: './public-top-navigation.component.html',
  styleUrls: ['./public-top-navigation.component.scss'],
})
export class PublicTopNavigationComponent extends HasSubscriptionsToClose() implements AfterViewInit {
  /**
   * The current items available in the Navbar
   * Each item represents a navigation option with its associated action
   */
  public readonly items: PopoverMenuItem[] = [
    {
      title: 'About',
      action: {
        type: 'route',
        url: 'about',
      },
    },
    {
      title: 'LTC Planning',
      route_prefix: ['article'],
      omit_base_action_url: true,
      action: {
        type: 'menu',
        url: 'articles',
      },
    },
    {
      title: 'Carriers',
      action: {
        type: 'route',
        url: 'carriers',
      },
    },
    {
      title: 'Calculators',
      classes: 'hidden-md',
      action: {
        type: 'iframe',
        url: '/calculators',
      },
    },
    {
      title: 'Cost of Care',
      action: {
        type: 'content_outlet',
        url: 'app-cost-of-care-map',
      },
    },
  ];

  /**
   * The public navbar items available in the Navbar
   * This array is dynamically filtered based on screen size
   */
  public public_nav_items: PopoverMenuItem[] = [];

  /**
   * Indicates if the Navbar is currently opaque with the close button visible
   */
  public is_opaque = false;

  /**
   * Observable of the current user's authentication state
   * Maps the user object to an AuthButtonState (LOGIN or DASHBOARD)
   */
  public authState$ = this.authenticationService.user.pipe(
    map((user) => (user ? AuthButtonState.DASHBOARD : AuthButtonState.LOGIN)),
    catchError(() => {
      console.error('Error determining auth state');
      return EMPTY;
    }),
  );

  /**
   * Indicates if the sidebar is currently visible
   */
  public sidebar_visible = false;

  /**
   * Reference to the cost of care popover component for managing its visibility
   * @private
   */
  @ViewChild('cost_of_care_popover')
  private cost_of_care_popover!: ContentOutletComponent;

  /**
   * Reference to the book us popover component for managing its visibility
   * @private
   */
  @ViewChild('popover_book_us')
  private popover_book_us!: ContentOutletComponent;

  /**
   * Environment configuration object for accessing environment-specific settings
   * @public
   * @readonly
   */
  public readonly env = environment;

  constructor(
    private readonly authenticationService: AuthenticationService,
    public readonly navigationService: NavigationService,
    private readonly elementRef: ElementRef,
    private readonly router: Router,
    private readonly breakpointWatcher: BreakpointWatcherService,
  ) {
    super();
    this.initializeSubscriptions();
  }

  /**
   * Initializes component subscriptions for navigation and breakpoint watching.
   * Uses subscribeTo for consistent subscription management and cleanup.
   * @private
   */
  private initializeSubscriptions(): void {
    // Subscribe to navigation opacity changes
    this.subscribeTo(
      this.navigationService.opaque$
        .pipe(
          catchError((error) => {
            console.error('Error in navigation opaque subscription:', error);
            return EMPTY;
          }),
        )
        .subscribe((opaque) => {
          this.is_opaque = opaque;
        }),
    );

    // Subscribe to breakpoint changes for responsive navigation
    this.subscribeTo(
      this.breakpointWatcher
        .watchBreakPoints()
        .pipe(
          catchError((error) => {
            console.error('Error in breakpoint subscription:', error);
            return EMPTY;
          }),
        )
        .subscribe((screen) => {
          this.public_nav_items = screen === 'mobile' ? this.items.filter((item) => item.title !== 'Calculators') : [...this.items];
        }),
    );
  }

  /**
   * After view initialization, sets the navbar height as a CSS variable.
   * This height is used for layout calculations throughout the application.
   */
  ngAfterViewInit(): void {
    try {
      const height = this.elementRef.nativeElement.offsetHeight;
      document.documentElement.style.setProperty('--navbar-height', `${height || 144}px`);
    } catch (error) {
      console.error('Error setting navbar height:', error);
    }
  }

  /**
   * Opens a content outlet popover component.
   * @param {ContentOutletComponent} popover - The content outlet component to open
   * @throws {Error} If popover is undefined
   */
  public openPopover(popover: ContentOutletComponent): void {
    if (!popover) {
      console.error('Attempted to open undefined popover');
      return;
    }
    this.navigationService.openPopover(popover);
  }

  /**
   * Loads a content outlet based on the menu item configuration.
   * Sets the navigation to opaque state and opens the appropriate popover.
   * @param {PopoverMenuItem} item - The menu item containing the content outlet configuration
   */
  public loadContentOutlet(item: PopoverMenuItem): void {
    if (!item?.action?.url) {
      console.error('Invalid menu item configuration');
      return;
    }

    this.is_opaque = true;

    try {
      switch (item.action.url) {
        case 'app-cost-of-care-map':
          this.cost_of_care_popover?.open();
          break;
        case 'app-book-us':
          this.popover_book_us?.open();
          break;
        default:
          console.warn(`Unknown content outlet: ${item.action.url}`);
      }
    } catch (error) {
      console.error('Error loading content outlet:', error);
    }
  }

  /**
   * Handles authentication button clicks by navigating to appropriate routes.
   * @param {AuthButtonState | null} state - The authentication state button that was clicked
   * @returns {Promise<boolean>} Navigation result
   */
  public async onAuthClick(state: AuthButtonState | null): Promise<boolean> {
    try {
      if (state === AuthButtonState.LOGIN) {
        return await this.router.navigate(['/auth/login']);
      }
      return await this.authenticationService.handleRedirect();
    } catch (error) {
      console.error('Error handling auth click:', error);
      return false;
    }
  }

  /**
   * Closes all active popovers in the navigation.
   * Used when cleaning up or resetting the navigation state.
   */
  public onCloseClick(): void {
    try {
      this.cost_of_care_popover?.close();
      this.popover_book_us?.close();
    } catch (error) {
      console.error('Error closing popovers:', error);
    }
  }
}
